import React from "react";
import {
  QueryCreate,
  QueryClauseItem as CoreQueryClauseItem,
  GroupedQueryClauseItem as CoreGroupedQueryClauseItem,
  QueryPreviewTable,
} from "@veiligh/reporting-core/ui";
import {
  useReportingContext,
  FormWizard,
  InputTypes,
} from "@veiligh/reporting-core/ui";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { HiOutlineSave } from "react-icons/hi";

import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Icon,
  Checkbox,
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  ToolTipEllipsis,
  ToolTipLists,
} from "~/components/ui";
import TableRowSkeleton from "~/components/shared/datatable/table-skeleton";
import StepWrapper from "~/components/pages/itemManagement/incidents/create/steps/wrapper";
import {
  DEFAULT_API_PAYLOAD,
  DATE_TIME,
  DATE_TIME_NULLABLE,
  IS_NULL_OPERATOR,
  IS_NOT_NULL_OPERATOR,
  SYSTEM_BOOLEAN,
  QUERY_GROUPED_DROPDOWN_OPTIONS,
} from "~/lib/constants";
import {
  CreateQueryValidator,
  SaveQueryValidator,
} from "~/components/pages/reports_old/query/create/type";
import { getModelActionsUrl } from "~/lib/utils";
import { handleError } from "~/lib/error";
import { useDispatch } from "react-redux";
import { useSelector } from "~/redux/store";
import { commonActions } from "~/redux/slice/common";
import type { QueryStore } from "~/components/pages/reports_old/query/types";

const ReportsNewQueryCreatePage: React.FC = () => {
  const { dataAdapter } = useReportingContext();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const queryFieldsStore = useSelector((state) => state.common.queryFields);
  const queryOperatorsStore = useSelector(
    (state) => state.common.queryOperators,
  );

  const handleSetQueryField = React.useCallback(
    (payload: { parameterType: string; data: any[] }) => {
      dispatch(
        commonActions.setQueryField({
          parameterType: payload.parameterType as keyof QueryStore,
          data: payload.data,
        }),
      );
    },
    [dispatch],
  );

  const QueryClauseItemBridge = React.useCallback((props: any) => {
    return (
      <CoreQueryClauseItem
        {...props}
        QUERY_GROUPED_DROPDOWN_OPTIONS={QUERY_GROUPED_DROPDOWN_OPTIONS}
        IS_NULL_OPERATOR={IS_NULL_OPERATOR}
        IS_NOT_NULL_OPERATOR={IS_NOT_NULL_OPERATOR}
        DATE_TIME={DATE_TIME}
        DATE_TIME_NULLABLE={DATE_TIME_NULLABLE}
        SYSTEM_BOOLEAN={SYSTEM_BOOLEAN}
      />
    );
  }, []);

  const GroupedQueryClauseItemBridge = React.useCallback((props: any) => {
    return (
      <CoreGroupedQueryClauseItem
        {...props}
        QUERY_GROUPED_DROPDOWN_OPTIONS={QUERY_GROUPED_DROPDOWN_OPTIONS}
        IS_NULL_OPERATOR={IS_NULL_OPERATOR}
        IS_NOT_NULL_OPERATOR={IS_NOT_NULL_OPERATOR}
        DATE_TIME={DATE_TIME}
        DATE_TIME_NULLABLE={DATE_TIME_NULLABLE}
        SYSTEM_BOOLEAN={SYSTEM_BOOLEAN}
      />
    );
  }, []);

  const QueryPreviewTableBridge = React.useCallback(
    (props: any) => (
      <QueryPreviewTable
        {...props}
        getModelActionsUrl={getModelActionsUrl}
        toast={toast}
        TableComponent={Table}
        TableHeaderComponent={TableHeader}
        TableBodyComponent={TableBody}
        TableHeadComponent={TableHead}
        TableRowComponent={TableRow}
        TableCellComponent={TableCell}
        ToolTipEllipsisComponent={ToolTipEllipsis}
        ToolTipListsComponent={ToolTipLists}
        TableRowSkeletonComponent={TableRowSkeleton}
      />
    ),
    [],
  );

  const dataAdapterBridge = React.useMemo(() => {
    return {
      getQueryItemTypes: async (_payload?: any) => {
        void _payload;
        const result = await dataAdapter.getQueryItemTypes();
        return { result };
      },
      getQueryFields: async (payload: { parameterType: string }) => {
        if (!payload?.parameterType) return { result: [] };
        const result = await dataAdapter.getQueryFields(
          payload.parameterType as keyof QueryStore,
        );
        return { result };
      },
      getCollections: async (payload: any) => {
        const request = payload?.getCollectionRequest ?? payload;
        const result = await dataAdapter.getCollections(request);
        return { result };
      },
      runQuery: async (payload: any) => {
        const result = await dataAdapter.runQuery(
          payload?.queryNodeDto ?? payload,
        );
        return { result };
      },
      saveQuery: async (payload: any) => {
        await dataAdapter.saveQuery(payload?.createQueryRequest ?? payload);
      },
    };
  }, [dataAdapter]);

  return (
    <QueryCreate
      dataAdapter={dataAdapterBridge}
      queryFieldsArray={queryFieldsStore}
      queryOperatorsArray={queryOperatorsStore}
      setQueryField={handleSetQueryField}
      DEFAULT_API_PAYLOAD={DEFAULT_API_PAYLOAD}
      QueryClauseItemComponent={QueryClauseItemBridge}
      GroupedQueryClauseItemComponent={GroupedQueryClauseItemBridge}
      QueryPreviewTableComponent={QueryPreviewTableBridge}
      FormWizardComponent={FormWizard}
      StepWrapperComponent={StepWrapper}
      ButtonComponent={Button}
      DialogComponent={Dialog}
      DialogContentComponent={DialogContent}
      DialogTitleComponent={DialogTitle}
      IconComponent={Icon}
      CheckboxComponent={Checkbox}
      InputTypes={InputTypes}
      SaveIconComponent={HiOutlineSave}
      toast={toast}
      CreateQueryValidator={CreateQueryValidator}
      SaveQueryValidator={SaveQueryValidator}
      handleError={handleError}
      onNavigateBack={() => navigate(-1)}
      onNavigateToQueryList={() => navigate("/reports/query")}
    />
  );
};

export default ReportsNewQueryCreatePage;
